import { useId, type ReactNode } from 'react';

interface CardSectionProps {
  /** Omit only to keep a subtree mounted while its title comes and goes (a wrapper whose element type
   *  changes remounts everything under it). A section the user sees always has a title. */
  title?: string;
  /** One line of sub-copy under the heading. */
  description?: string;
  /** Controls for the whole section (Refresh, Import), right-aligned beside the heading. */
  actions?: ReactNode;
  /** Test id on the section, for specs that scope to one group of a card. */
  testId?: string;
  /** Id on the heading, for a spec or `aria-labelledby` that already names it. Generated when left out. */
  id?: string;
  children: ReactNode;
}

/**
 * The one titled group inside a card (DESIGN.md §1.5). Sibling sections stack with a divider
 * between them; put them in a `CardBody className="space-y-6"` so the gap and the divider agree.
 */
export function CardSection({
  title,
  description,
  actions,
  testId,
  id,
  children,
}: CardSectionProps) {
  const generatedId = useId();
  const headingId = id ?? generatedId;

  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      data-testid={testId}
      className="space-y-4 border-t border-gray-200 pt-6 first:border-t-0 first:pt-0 dark:border-gray-800"
    >
      {title && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 id={headingId} className="text-base font-semibold text-gray-800 dark:text-white/90">
              {title}
            </h4>
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
